import {
  type $Typed,
  AppBskyEmbedExternal,
  AppBskyEmbedImages,
  AppBskyEmbedRecord,
  AppBskyEmbedRecordWithMedia,
  AppBskyEmbedVideo,
  AppBskyFeedDefs,
  AppBskyFeedPost,
  AppBskyGraphDefs,
  AppBskyLabelerDefs,
} from '@atproto/api'

export type Embed =
  | {
      type: 'post'
      view: $Typed<AppBskyEmbedRecord.ViewRecord>
    }
  | {
      type: 'post_not_found'
      view: $Typed<AppBskyEmbedRecord.ViewNotFound>
    }
  | {
      type: 'post_blocked'
      view: $Typed<AppBskyEmbedRecord.ViewBlocked>
    }
  | {
      type: 'post_detached'
      view: $Typed<AppBskyEmbedRecord.ViewDetached>
    }
  | {
      type: 'feed'
      view: $Typed<AppBskyFeedDefs.GeneratorView>
    }
  | {
      type: 'list'
      view: $Typed<AppBskyGraphDefs.ListView>
    }
  | {
      type: 'labeler'
      view: $Typed<AppBskyLabelerDefs.LabelerView>
    }
  | {
      type: 'starter_pack'
      view: $Typed<AppBskyGraphDefs.StarterPackViewBasic>
    }
  | {
      type: 'images'
      view: $Typed<AppBskyEmbedImages.View>
    }
  | {
      type: 'link'
      view: $Typed<AppBskyEmbedExternal.View>
    }
  | {
      type: 'video'
      view: $Typed<AppBskyEmbedVideo.View>
    }
  | {
      type: 'post_with_media'
      view: Embed
      media: Embed
    }
  | {
      type: 'document'
      view: {
        $type: 'app.bsky.embed.document#view'
        cid: string
        manifestUri?: string
        mimeType: string
        title?: string
        pageCount?: number
        fileSize?: number
        alt?: string
        thumbnailUri?: string | null
      }
    }
  | {
      type: 'unknown'
      view: null
    }

export type EmbedType<T extends Embed['type']> = Extract<Embed, {type: T}>

export function parseEmbedRecordView({record}: AppBskyEmbedRecord.View): Embed {
  if (AppBskyEmbedRecord.isViewRecord(record)) {
    return {
      type: 'post',
      view: record,
    }
  } else if (AppBskyEmbedRecord.isViewNotFound(record)) {
    return {
      type: 'post_not_found',
      view: record,
    }
  } else if (AppBskyEmbedRecord.isViewBlocked(record)) {
    return {
      type: 'post_blocked',
      view: record,
    }
  } else if (AppBskyEmbedRecord.isViewDetached(record)) {
    return {
      type: 'post_detached',
      view: record,
    }
  } else if (AppBskyFeedDefs.isGeneratorView(record)) {
    return {
      type: 'feed',
      view: record,
    }
  } else if (AppBskyGraphDefs.isListView(record)) {
    return {
      type: 'list',
      view: record,
    }
  } else if (AppBskyLabelerDefs.isLabelerView(record)) {
    return {
      type: 'labeler',
      view: record,
    }
  } else if (AppBskyGraphDefs.isStarterPackViewBasic(record)) {
    return {
      type: 'starter_pack',
      view: record,
    }
  } else {
    return {
      type: 'unknown',
      view: null,
    }
  }
}

export function parseEmbed(embed: AppBskyFeedDefs.PostView['embed']): Embed {
  if (AppBskyEmbedImages.isView(embed)) {
    return {
      type: 'images',
      view: embed,
    }
  } else if (AppBskyEmbedExternal.isView(embed)) {
    return {
      type: 'link',
      view: embed,
    }
  } else if (AppBskyEmbedVideo.isView(embed)) {
    return {
      type: 'video',
      view: embed,
    }
  } else if (
    typeof embed === 'object' &&
    embed !== null &&
    ((embed as {$type?: string}).$type === 'app.bsky.embed.document#view' ||
      (embed as {$type?: string}).$type === 'app.bsky.embed.document')
  ) {
    // Handle both the server view (#view) and the raw record embed (no #view suffix).
    // The appview does not know this custom lexicon type, so post.embed is absent and
    // the raw record.embed is what we get when falling back via resolvePostEmbed().
    const raw = embed as {
      $type: string
      blob?: {ref?: {$link?: string}}
      cid?: string
      mimeType?: string
      title?: string
      pageCount?: number
      fileSize?: number
      manifestUri?: string
      thumbnailUri?: string | null
      alt?: string
    }
    const blobRef = raw.blob?.ref
    const cidFromRef =
      blobRef != null
        ? typeof blobRef.$link === 'string'
          ? (blobRef as {$link: string}).$link
          : String(blobRef)
        : undefined
    return {
      type: 'document',
      view: {
        $type: 'app.bsky.embed.document#view' as const,
        cid: raw.cid ?? cidFromRef ?? '',
        mimeType: raw.mimeType ?? 'application/pdf',
        title: raw.title,
        pageCount: raw.pageCount,
        fileSize: raw.fileSize,
        manifestUri: raw.manifestUri,
        thumbnailUri: raw.thumbnailUri,
        alt: raw.alt,
      },
    }
  } else if (AppBskyEmbedRecord.isView(embed)) {
    return parseEmbedRecordView(embed)
  } else if (AppBskyEmbedRecordWithMedia.isView(embed)) {
    return {
      type: 'post_with_media',
      view: parseEmbedRecordView(embed.record),
      media: parseEmbed(embed.media),
    }
  } else {
    return {
      type: 'unknown',
      view: null,
    }
  }
}

/**
 * Returns the embed that should be rendered for a post.
 *
 * post.embed is the server-generated view, but the appview does not know
 * app.bsky.embed.document (a custom lexicon), so it never populates post.embed
 * for document posts. In that case we fall back to record.embed and let
 * parseEmbed synthesize a document view from the raw blob metadata.
 */
export function resolvePostEmbed(
  post: AppBskyFeedDefs.PostView,
): AppBskyFeedDefs.PostView['embed'] {
  if (post.embed) return post.embed
  if (AppBskyFeedPost.isRecord(post.record)) {
    const re = post.record.embed as {$type?: string} | undefined
    if (re?.$type === 'app.bsky.embed.document') {
      return re as AppBskyFeedDefs.PostView['embed']
    }
  }
  return post.embed
}
